<script setup lang="ts">
import './assets/style.scss'
import { useObjectSubTypeSelect } from './../model/model.ts'
import { VIcons } from '@/shared/ui'
import { computed, type PropType, watch } from 'vue'
import { useAuth } from '@/entities/Auth'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  all: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  /** Sub-type ids to hide from the menu (e.g. already used by another row). */
  excludeSubTypeIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  emptyValue: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  filtered: {
    type: Boolean,
    default: false,
  },
})

const model = useObjectSubTypeSelect()
const auth = useAuth()
const value = defineModel<any>()
const emits = defineEmits(['selected', 'resolved', 'change'])

const { isPending } = model.loadData()

function subTypesForMenuItem(item: { sub_types?: { id: number; name: string }[] }) {
  const subs = item.sub_types || []
  if (!props.excludeSubTypeIds?.length) return subs
  const exclude = new Set(props.excludeSubTypeIds.map((id) => Number(id)).filter((id) => id > 0))
  return subs.filter((s) => !exclude.has(Number(s.id)))
}

function shouldShowMenuItem(item: { sub_types?: unknown[] }) {
  const subs = item.sub_types
  if (!subs || subs.length === 0) return props.all
  return subTypesForMenuItem(item as { sub_types?: { id: number; name: string }[] }).length > 0
}

const listSubtypes = computed(() => {
  const list: any[] = []

  model.items.forEach((item) => {
    ;(item.sub_types || []).forEach((subType: any) => {
      list[subType.id] = subType
    })
  })

  return list
})

const currentSubType = computed(() => {
  if (!value.value || !listSubtypes.value[value.value]) return

  return listSubtypes.value[value.value]
})

const currentSubTypeName = computed(() => currentSubType.value?.name)

watch(
  currentSubType,
  (item) => {
    if (item) emits('resolved', item)
  },
  { immediate: true },
)

/**
 * Функция выбора типа элемента списка
 * @param sybType
 */
const selectItemHandler = (sybType: any) => {
  value.value = sybType.id

  emits('selected', sybType)
  emits('change')
}

/**
 * Обрабатываем клик по родителю
 * @param event
 * @param item
 */
const selectParentItemHandler = (event: MouseEvent, item) => {
  if (item.sub_types?.length > 1) return

  event.preventDefault()
  selectItemHandler(item.sub_types[0])
}

/**
 * Фильтруем данный для неполного вывода списка
 */
const itemsList = computed(() => {
  if (props.filtered) {
    return model.items.filter(
      (item) => item.is_show_filter && auth.hasElementPermission(item.id, 'read'),
    )
  }

  return model.items
})
</script>

<template>
  <VMenu>
    <template #activator="{ props }">
      <VTextField
        v-bind="disabled ? {} : props"
        class="object-subtype-select__input"
        :loading="isPending"
        :label="label"
        :model-value="currentSubTypeName"
        :hide-details="hideDetails"
        :disabled
        readonly
      >
        <template #append-inner>
          <VIcons type="arrow_drop_down" />
        </template>
      </VTextField>
    </template>

    <VList :disabled="readonly">
      <VListItem v-if="emptyValue" @click="selectItemHandler({ id: 0 })">
        <div class="object-subtype-select__menu-item object-subtype-select__menu-item_inactive">
          <VIcons type="close" /> {{ $t('itf.not_type') }}
        </div>
      </VListItem>

      <template v-for="item in itemsList" :key="item.name">
        <VListItem
          v-if="shouldShowMenuItem(item)"
          @click="(event: any) => selectParentItemHandler(event, item)"
        >
          <template #title>
            <div class="object-subtype-select__menu-item">
              <VIcons :type="item.icon" /> {{ item.object_name }}
            </div>
          </template>

          <template #append>
            <VIcons v-if="item.sub_types.length > 1 || !compact" type="arrow_right" />
          </template>

          <VMenu
            v-if="item.sub_types.length > 1 || !compact"
            activator="parent"
            open-on-hover
            submenu
          >
            <VList>
              <VListItem
                v-for="subItem in subTypesForMenuItem(item)"
                :key="subItem.id"
                :title="subItem.name"
                @click="selectItemHandler(subItem)"
              />
            </VList>
          </VMenu>
        </VListItem>
      </template>
    </VList>
  </VMenu>
</template>
